
import {
  Vue, Component, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import EditLeadSupplyDialog from '@/components/EditLeadSupplyDialog.vue';
import PermissionsInfo from '@/components/PermissionsInfo.vue';
import SupplyHistoryIcon from '@/components/SupplyHistoryIcon.vue';
import {
  Lead, PermissionCode, ProjectsPool, Task,
} from '@/types';
import UTILS from '@/lib/UTILS';
import API from '@/lib/API';
import { currentUserHasPermission, formatEuro } from '@/helpers';

moment.locale('it');

@Component({
  components: {
    EditLeadSupplyDialog,
    PermissionsInfo,
    SupplyHistoryIcon,
  },
})
export default class LeadSupply extends Vue {
  editDialog = false

  // Dati supply caricati dall'API come fallback
  fatherSupplyData: {
    supply1?: boolean;
    supply1text?: string;
    supply2?: boolean;
    supply2text?: string;
    supply3?: boolean;
    supply3text?: string;
    supply4?: boolean;
    supply4text?: string;
    supply5?: boolean;
    supply5text?: string;
    supplyNotes?: string;
  } | null = null;

  loading = false;

  get pool(): ProjectsPool {
    return this.$store.getters.currentPool;
  }

  get currentLead() {
    return this.$store.getters.currentLead;
  }

  get lead() {
    return this.isFather
      ? this.currentLead
      : this.father;
  }

  get father(): Lead | null {
    const firstTask: Task = this.currentLead.Tasks[0];
    const { fatherLeadId } = firstTask;

    return fatherLeadId && fatherLeadId in this.pool
      ? this.pool[fatherLeadId]
      : null;
  }

  get isChild(): boolean {
    return ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead?.type);
  }

  get isFather(): boolean {
    return !this.isChild;
  }

  get shouldShowCard(): boolean {
    // Mostra la card se:
    // 1. Abbiamo accesso al padre (lead padre o figlia con accesso)
    // 2. Oppure siamo una lead figlia e abbiamo caricato i dati via API
    return !!this.lead || (this.isChild && !!this.fatherSupplyData);
  }

  get isEditable() {
    // Se abbiamo accesso al lead, controlla il suo status
    if (this.lead) {
      return this.lead
        && 'status' in this.lead
        && (this.lead.status === 'open' || this.lead.status === 'new')
        && (
          this.hasPermission('supply.edit')
          || this.hasPermission('targetDate.edit')
          || this.hasPermission('orderConfirmationDate.edit')
          || this.hasPermission('firstPmClientContactDate.edit')
          || this.hasPermission('totalContractValue.edit')
        );
    }

    // Se non abbiamo accesso al lead ma abbiamo i dati dall'API,
    // controlla lo status della currentLead (lead figlia)
    if (this.fatherSupplyData && this.currentLead) {
      return this.currentLead
        && 'status' in this.currentLead
        && (this.currentLead.status === 'open' || this.currentLead.status === 'new')
        && (this.hasPermission('supply.edit'));
    }

    return false;
  }

  // Ritorna true se il Totale contrattuale è visibile
  get isTotalContractValueVisible() {
    return this.hasPermission('totalContractValue.show');
  }

  // Ritorna true se la Data obiettivo per il completamento della commessa è visibile
  get isTargetDateVisible() {
    return this.hasPermission('targetDate.show');
  }

  // Ritorna true se la Data conferma d'ordine è visibile
  get isOrderConfirmationDateVisible() {
    return this.hasPermission('orderConfirmationDate.show');
  }

  // Ritorna true se la Data primo contatto PM-Cliente è visibile
  get isFirstPmClientContactDateVisible() {
    return this.hasPermission('firstPmClientContactDate.show');
  }

  // Ritorna true se l'Oggetto della fornitura è visibile
  get isSupplyVisible() {
    return this.hasPermission('supply.show');
  }

  get title(): string {
    const hasTargetDate = this.hasPermission('targetDate.show');
    const hasOrderConfirmation = this.hasPermission('orderConfirmationDate.show');
    const hasFirstPmClientContact = this.hasPermission('firstPmClientContactDate.show');
    const hasContractValue = this.hasPermission('totalContractValue.show');
    const hasSupply = this.hasPermission('supply.show');

    const parts: string[] = [];

    if (hasOrderConfirmation) parts.push('data conferma d\'ordine');
    if (hasTargetDate) parts.push('data obiettivo');
    if (hasFirstPmClientContact) parts.push('data 1° contatto PM-Cliente');
    if (hasContractValue) parts.push('totale €');
    if (hasSupply) parts.push('oggetto fornitura');

    if (parts.length === 0) return 'Dati commessa';
    // eslint-disable-next-line prefer-template
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    // eslint-disable-next-line prefer-template
    if (parts.length === 2) return parts[0].charAt(0).toUpperCase() + parts[0].slice(1) + ' e ' + parts[1];

    const lastPart = parts.pop();
    // eslint-disable-next-line prefer-template
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ') + ' e ' + lastPart;
  }

  // Ritorna true se non ci sono informazioni da mostrare
  get noData() {
    if (this.isTotalContractValueVisible && (this.totalContractValue || this.totalContractValueNotes)) return false;
    if (this.isTargetDateVisible && this.targetDate) return false;
    if (this.isOrderConfirmationDateVisible && this.orderConfirmationDate) return false;
    if (this.isFirstPmClientContactDateVisible && this.firstPmClientContactDate) return false;
    if (this.isSupplyVisible && this.supplyText) return false;
    return true;
  }

  get targetDate() {
    return this.getCustomValueDateByName('targetDate');
  }
  get targetReminder() {
    return this.getCustomValueBooleanByName('targetReminder');
  }

  get orderConfirmationDate() {
    return this.getCustomValueDateByName('orderConfirmationDate');
  }

  get firstPmClientContactDate() {
    return this.getCustomValueDateByName('firstPmClientContactDate');
  }

  get totalContractValue() {
    return this.getCustomValueTextByName('totalContractValue');
  }

  get totalContractValueNotes() {
    return this.getCustomValueTextByName('totalContractValueNotes');
  }

  get supply1() {
    return this.lead ? this.getCustomValueBooleanByName('supply1') : (this.fatherSupplyData?.supply1 || false);
  }
  get supply2() {
    return this.lead ? this.getCustomValueBooleanByName('supply2') : (this.fatherSupplyData?.supply2 || false);
  }
  get supply3() {
    return this.lead ? this.getCustomValueBooleanByName('supply3') : (this.fatherSupplyData?.supply3 || false);
  }
  get supply4() {
    return this.lead ? this.getCustomValueBooleanByName('supply4') : (this.fatherSupplyData?.supply4 || false);
  }
  get supply5() {
    return this.lead ? this.getCustomValueBooleanByName('supply5') : (this.fatherSupplyData?.supply5 || false);
  }

  get supply1text() {
    return this.lead ? this.getCustomValueTextByName('supply1text') : (this.fatherSupplyData?.supply1text || '');
  }
  get supply2text() {
    return this.lead ? this.getCustomValueTextByName('supply2text') : (this.fatherSupplyData?.supply2text || '');
  }
  get supply3text() {
    return this.lead ? this.getCustomValueTextByName('supply3text') : (this.fatherSupplyData?.supply3text || '');
  }
  get supply4text() {
    return this.lead ? this.getCustomValueTextByName('supply4text') : (this.fatherSupplyData?.supply4text || '');
  }
  get supply5text() {
    return this.lead ? this.getCustomValueTextByName('supply5text') : (this.fatherSupplyData?.supply5text || '');
  }

  get supplyText() {
    if (this.supply5 && !!this.supply5text) return this.supply5text.replace('\n', '<br>');
    if (this.supply4 && !!this.supply4text) return this.supply4text.replace('\n', '<br>');
    if (this.supply3 && !!this.supply3text) return this.supply3text.replace('\n', '<br>');
    if (this.supply2 && !!this.supply2text) return this.supply2text.replace('\n', '<br>');
    if (this.supply1 && !!this.supply1text) return this.supply1text.replace('\n', '<br>');
    return null;
  }

  get supplyLabel() {
    if (this.supply5 && !!this.supply5text) return 'Fornitura definitiva';
    if (this.supply4 && !!this.supply4text) return 'Fornitura pattuita';
    if (this.supply3 && !!this.supply3text) return 'Disponibilità materiali';
    if (this.supply2 && !!this.supply2text) return 'Progettazione preliminare';
    if (this.supply1 && !!this.supply1text) return 'Fornitura contrattuale';
    return null;
  }

  mDate(date: Date) {
    return moment(date).format('L');
  }

  formatEuro(value: string | null) {
    return formatEuro(value);
  }

  getCustomValueTextByName(name: string): string | null {
    if (!this.lead) return null;
    return UTILS.getCustomValueTextByName(this.lead, name) || null;
  }

  getCustomValueBooleanByName(name: string): boolean | null {
    if (!this.lead) return null;
    return UTILS.getCustomValueBooleanByName(this.lead, name);
  }

  getCustomValueDateByName(name: string): Date | null {
    if (!this.lead) return null;
    return UTILS.getCustomValueDateByName(this.lead, name) || null;
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  async loadFatherSupply() {
    if (!this.currentLead || !this.isChild) return;

    this.loading = true;
    try {
      const result = await API.getLeadSupply(this.currentLead.id);
      if (!(result instanceof Error)) {
        this.fatherSupplyData = {
          supply1: !!result.supply1,
          supply1text: result.supply1text || '',
          supply2: !!result.supply2,
          supply2text: result.supply2text || '',
          supply3: !!result.supply3,
          supply3text: result.supply3text || '',
          supply4: !!result.supply4,
          supply4text: result.supply4text || '',
          supply5: !!result.supply5,
          supply5text: result.supply5text || '',
          supplyNotes: result.supplyNotes || '',
          // Nota: targetDate, orderConfirmationDate, firstPmClientContactDate, totalContractValue
          // non sono disponibili nell'API getLeadSupply, quindi rimangono undefined
        };
      }
    } catch (error) {
      console.error('Errore nel caricamento dei dati supply:', error);
    } finally {
      this.loading = false;
    }
  }

  @Watch('currentLead')
  async onCurrentLeadChange() {
    if (this.currentLead && this.isChild && !this.lead) {
      // Se è una lead figlia e non abbiamo accesso al padre, carica i dati via API
      await this.loadFatherSupply();
    }
  }

  @Watch('editDialog')
  async onEditDialogChange(val: boolean) {
    // Quando il dialog si chiude (val = false), ricarica i dati se necessario
    if (!val) {
      if (this.isChild && !this.lead) {
        // Lead figlia senza accesso al padre: ricarica via API
        await this.loadFatherSupply();
      } else if (this.lead) {
        // Lead padre o figlia con accesso al padre: ricarica dal store
        await this.$store.dispatch('refreshLead');
      }
    }
  }

  async mounted() {
    if (this.currentLead && this.isChild && !this.lead) {
      // Se è una lead figlia e non abbiamo accesso al padre, carica i dati via API
      await this.loadFatherSupply();
    }
  }

  // Metodo pubblico per permettere il refresh dei dati
  async refreshSupply() {
    await this.loadFatherSupply();
  }

  onEdit() {
    this.editDialog = true;
  }
}
